import { FOOTPRINT_DEFAULT_CELL, footprintCellLimits } from "./footprint-cell.ts";

/**
 * The Behind and In front marks of a frame (ADR-132 decision 2): cells of the footprint's own grid, `[column,
 * rowsUp]` from the frame's bottom-left in the entry's cell size (ADR-040, ADR-113 decision 7), beside the cells
 * the frame blocks. Collision and drawing are two layers (decision 14): a cell may be blocked and also Behind or In front,
 * and is never both Behind and In front. Unlike a blocked cell they may lie outside the art, up to
 * `STAND_REACH` cells on each side, so a builder can paint the path before a door or the space behind a wall.
 */

/**
 * How many cells outside the frame's cell grid a mark may lie, on each side.
 */
export const STAND_REACH = 8;

/**
 * The marks of an entry that `standMarksIssue` checks: the blocked cells, the two marks, and the entry's cell.
 */
export interface StandCellLists {
  readonly cells?: readonly (readonly [number, number])[];
  readonly behind?: readonly (readonly [number, number])[];
  readonly front?: readonly (readonly [number, number])[];
  readonly cell?: number;
}

/**
 * The frame a mark list is checked against: its size in pixels.
 */
export interface StandFrame {
  readonly w: number;
  readonly h: number;
}

/**
 * Whether a value is one mark cell: a pair of whole numbers from `-STAND_REACH` up.
 *
 * @param value The value.
 * @returns True for a cell.
 */
function isStandCell(value: unknown): value is [number, number] {
  return Array.isArray(value) && value.length === 2 && value.every((n) => typeof n === "number" && Number.isInteger(n) && n >= -STAND_REACH);
}

/**
 * Reads a list of mark cells: `[column, rowsUp]` pairs of whole numbers, none below `-STAND_REACH`.
 *
 * @param value Candidate value.
 * @param path Path for the error message.
 * @returns The cells, copied, in the order given.
 * @throws Error naming the path when the value is not such a list.
 */
export function readStandCells(value: unknown, path: string): [number, number][] {
  if (!Array.isArray(value) || !value.every(isStandCell)) {
    throw new Error(`${path} must be [[column, rowsUp], ...] in whole numbers from -${STAND_REACH}`);
  }
  return value.map((cell) => [cell[0], cell[1]]);
}

/**
 * Whether two cell lists share a cell.
 *
 * @param a One list.
 * @param b The other.
 * @returns True when a cell lies in both lists.
 */
function share(a: readonly (readonly [number, number])[] | undefined, b: readonly (readonly [number, number])[] | undefined): boolean {
  if (a === undefined || b === undefined || a.length === 0 || b.length === 0) {
    return false;
  }
  const keys = new Set(a.map((c) => `${c[0]},${c[1]}`));
  return b.some((c) => keys.has(`${c[0]},${c[1]}`));
}

/**
 * The first rule an entry's marks break, or none. `behind` and `front` share no cell; the blocked `cells` may share a
 * cell with either, since collision and drawing are two layers. Each list holds at most the
 * cells the entry's cell size allows (`footprintCellLimits`). With a frame, every cell lies within `STAND_REACH`
 * cells of the frame's grid in the entry's own cell.
 *
 * @param entry The entry's lists and cell.
 * @param frame The frame, or undefined where its size is not known.
 * @returns The reason, or undefined when the marks are well formed.
 */
export function standMarksIssue(entry: StandCellLists, frame?: StandFrame): string | undefined {
  if (share(entry.behind, entry.front)) {
    return "behind and front share a cell, and a cell is drawn behind or in front, never both";
  }
  const own = entry.cell ?? FOOTPRINT_DEFAULT_CELL;
  const { count, span } = footprintCellLimits(own);
  for (const [name, list] of [["behind", entry.behind], ["front", entry.front]] as const) {
    if (list === undefined) {
      continue;
    }
    if (list.length > count) {
      return `${name} holds ${list.length} cells and an entry takes at most ${count}`;
    }
    if (list.some((c) => c[0] >= span || c[1] >= span)) {
      return `${name} takes cell coordinates under ${span}`;
    }
    if (frame !== undefined) {
      const columns = Math.ceil(frame.w / own);
      const rows = Math.ceil(frame.h / own);
      const outside = list.find((c) => c[0] < -STAND_REACH || c[1] < -STAND_REACH || c[0] >= columns + STAND_REACH || c[1] >= rows + STAND_REACH);
      if (outside !== undefined) {
        return `${name} cell [${outside[0]}, ${outside[1]}] lies more than ${STAND_REACH} cells outside the frame's ${columns} by ${rows} cells`;
      }
    }
  }
  return undefined;
}
